/**
 * ConfirmCard — displays a pending write action for user confirmation.
 * Renders summary + key facts + Bestätigen/Ablehnen buttons.
 * [Source: Story P1b spec §8]
 */
import type { BrokUiMessage } from '../brok-chat.types';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';

type ConfirmMsg = Extract<BrokUiMessage, { kind: 'confirmation' }>;

interface Props {
  msg: ConfirmMsg;
  onConfirm: (toolCallId: string) => void;
  onReject: (toolCallId: string) => void;
}

export function ConfirmCard({ msg, onConfirm, onReject }: Props) {
  const isPending = msg.decision === 'pending';

  return (
    <Card className="p-3 text-sm">
      <p className="mb-2 font-medium">{msg.summary}</p>
      {Object.keys(msg.facts).length > 0 && (
        <dl className="mb-3 space-y-0.5 text-xs text-muted-foreground">
          {Object.entries(msg.facts).map(([k, v]) => (
            <div key={k} className="flex gap-1">
              <dt className="font-medium">{k}:</dt>
              <dd>{String(v)}</dd>
            </div>
          ))}
        </dl>
      )}
      {isPending ? (
        <div className="flex gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => onConfirm(msg.toolCallId)}
          >
            Bestätigen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReject(msg.toolCallId)}
          >
            Ablehnen
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {msg.decision === 'approved'
            ? 'Bestätigt.'
            : msg.decision === 'failed'
              ? 'Aktion fehlgeschlagen.'
              : msg.decision === 'rueckfrage'
                ? 'Rückfrage notiert.'
                : 'Abgelehnt.'}
        </p>
      )}
    </Card>
  );
}
